import { referenceDataApi } from '../../api/referenceData.js';
import { Card, ExportCsvButton } from '../../components/ui';
import { useApi } from '../../hooks';
import { downloadCsv } from '../../utils/csv.js';

// Built in the browser, not on the server: this endpoint returns every pair in
// one shot, so "all rows" and "the rows on screen" are the same set.
const CSV_COLUMNS = [
  { key: 'site_code',            label: 'Site' },
  { key: 'location_name',        label: 'Location (worked)' },
  { key: 'mirror_site_code',     label: 'Mirror Site' },
  { key: 'mirror_location_name', label: 'Mirror Location (billed)' },
  { key: 'mirror_location_id',   label: 'NS ID' },
  { key: 'status',               label: 'Status' },
  { key: 'applies',              label: 'Applied' },
  { key: 'loaned_records',       label: 'Loaned Records' },
];

// Read-only reference view. The pairs are NOT stored anywhere — the engine
// derives them from the NetSuite location catalog on every run (site code + 1,
// base names must match). This tab exists so billing can see which Canadian
// sites currently have a usable mirror and how much time the rule is moving.

const STATUS_COLOR = {
  Active: '#155724',
};

function statusStyle(item) {
  return {
    color: item.applies ? '#155724' : '#b25b00',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  };
}

export function MirrorLocationsExplorer() {
  const { data, loading, error } = useApi(referenceDataApi.getMirrorLocations, []);

  if (loading) return <div className="alert alert-info">Loading...</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  const items = data?.items || [];
  const s = data?.summary || {};

  return (
    <div>
      <div style={{
        marginBottom: 16, padding: '12px 14px', background: '#f4f7fd',
        border: '1px solid #d6e0f5', borderRadius: 6, fontSize: 13, lineHeight: 1.6,
      }}>
        <strong>Canada Mirror Locations</strong> — when an employee whose NetSuite
        home location is <em>outside</em> Canada works a Canadian site (5-digit
        site code starting <code>9</code>), the time is billed as a{' '}
        <strong>loaned employee</strong>. NetSuite will not accept the TimeBill
        against the Canadian entity&apos;s own location, so it books against that
        site&apos;s mirror — the matching <code>(PSC Group)</code> location, whose
        site code is the original <strong>+&nbsp;1</strong>.
        <div style={{ marginTop: 6, color: '#555' }}>
          Only the <strong>location on the NetSuite TimeBill</strong> changes. The
          rate, service item, cost center, area and project all still come from the
          site actually worked. Pairs are derived from NetSuite automatically —
          there is nothing to maintain here; add the mirror location in NetSuite and
          it appears on the next run.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <Card label="Canadian Sites" value={s.pairs_total ?? 0} />
        <Card label="Mirrors Active" value={s.pairs_active ?? 0} color={STATUS_COLOR.Active} />
        <Card label="Loaned Records" value={(s.loaned_records ?? 0).toLocaleString()} color="#0d47a1" />
        {s.unmirrored_records > 0 && (
          <Card label="Loaned, No Mirror" value={s.unmirrored_records.toLocaleString()} color="#b25b00" />
        )}
      </div>

      {items.length === 0 ? (
        <div className="alert alert-info">No Canadian (9xxxx) locations in NetSuite.</div>
      ) : (
        <>
        <div style={{ marginBottom: 8 }}>
          <ExportCsvButton
            onExport={() => downloadCsv('mirror-locations', items, CSV_COLUMNS)}
            title="Download every Canada mirror pair as CSV"
          />
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Site</th>
                <th>Location (worked)</th>
                <th style={{ textAlign: 'center' }}>→</th>
                <th>Mirror Site</th>
                <th>Mirror Location (billed)</th>
                <th>NS ID</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Loaned Records</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.site_code}>
                  <td style={{ fontWeight: 600 }}>{it.site_code}</td>
                  <td>{it.location_name || '-'}</td>
                  <td style={{ textAlign: 'center', color: '#888' }}>→</td>
                  <td style={{ fontWeight: 600 }}>
                    {it.applies ? it.mirror_site_code : (
                      <span style={{ color: '#999' }}>{it.mirror_site_code || '-'}</span>
                    )}
                  </td>
                  <td>{it.mirror_location_name || <span style={{ color: '#999' }}>—</span>}</td>
                  <td style={{ color: '#666' }}>{it.mirror_location_id ?? '-'}</td>
                  <td style={statusStyle(it)}>
                    {it.applies ? '✓ ' : '⚠ '}{it.status}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {it.loaned_records
                      ? it.loaned_records.toLocaleString()
                      : <span style={{ color: '#bbb' }}>0</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <div style={{ marginTop: 12, fontSize: 11, color: '#888', lineHeight: 1.6 }}>
        A pair is applied only when the <code>+1</code> location exists in NetSuite,
        is active, and its name matches the source location&apos;s (ignoring the
        trailing <code>(PSC Group)</code>). If a name stops matching, the pair is
        shown here as not applied rather than billing to a location that may belong
        to a different plant — loaned records at that site keep the site&apos;s own
        location and stay flagged on the Records tab.
      </div>
    </div>
  );
}
